import { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  looksLikeCoordinates,
  parseCoordinates,
} from '../../utils/coordinates';
import { geocodingService } from '../../services/geocodingService';
import {
  searchPinCleared,
  searchPinDropped,
} from '../../store/slices/mapViewSlice';

/** Street-level zoom applied when jumping to a result. */
const RESULT_ZOOM = 15;

interface MapSearchFieldProps {
  /**
   * The map to move. Held as a ref because it is only read on submit — by
   * which point Leaflet has long since attached it.
   */
  mapRef: React.MutableRefObject<L.Map | null>;
}

/** What the field is currently telling the operator, if anything. */
type Message = { kind: 'error' | 'found'; text: string };

/**
 * Floating map search: jump to a coordinate pair, street address, or postal
 * code (issue #402). Shared by the fleet map and the mission planner.
 *
 * Coordinates are resolved locally and never leave the browser; anything else
 * goes to the geocoder on submit — never per keystroke, which would be both a
 * poor fit for the provider's usage policy and a request per typo.
 */
export const MapSearchField: React.FC<MapSearchFieldProps> = ({ mapRef }) => {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);
  const dispatch = useDispatch();

  // Move the map AND mark the spot (#523). Moving alone left the operator
  // looking at a plausible map with nothing to say which building the result
  // meant, or whether the geocoder had understood them.
  const goTo = (lat: number, lng: number, label: string) => {
    mapRef.current?.setView([lat, lng], RESULT_ZOOM);
    dispatch(searchPinDropped({ lat, lng, label }));
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (trimmed === '' || searching) {
      return;
    }
    setMessage(null);

    const coordinates = parseCoordinates(trimmed);
    if (coordinates) {
      // Echo the parsed pair rather than the raw input: "52.2297N 21.0122E"
      // and "52.2297, 21.0122" are the same place, and the pin should say
      // which place it understood.
      goTo(
        coordinates.lat,
        coordinates.lng,
        `${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}`
      );
      return;
    }

    // Two numbers and nothing else was meant as coordinates, so say what is
    // wrong with them rather than asking a geocoder about "91, 200".
    if (looksLikeCoordinates(trimmed)) {
      setMessage({
        kind: 'error',
        text: 'Coordinates must be within ±90 latitude and ±180 longitude.',
      });
      return;
    }

    setSearching(true);
    try {
      const match = await geocodingService.search(trimmed);
      if (match) {
        goTo(match.lat, match.lng, match.label);
        setMessage({ kind: 'found', text: match.label });
      } else {
        setMessage({ kind: 'error', text: `No match for “${trimmed}”.` });
      }
    } catch {
      // The service has already logged the cause; the operator needs the
      // distinction between "not found" and "could not look up", not the stack.
      setMessage({
        kind: 'error',
        text: 'Location lookup is unavailable. Check your connection.',
      });
    } finally {
      setSearching(false);
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        // No positioning here (#518). This component used to place itself at
        // top-right with zIndex 1000, which meant two maps and every future
        // overlay had to know that and work around it — and one did not: the
        // fleet ownership filter landed on the same corner at the same depth
        // and covered the search box. A control does not get to decide where it
        // lives on someone else's map; the parent stacks it.
        p: 1,
        width: { xs: 200, sm: 268 },
        borderRadius: 2,
      }}
    >
      <Box
        component="form"
        onSubmit={onSubmit}
        sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}
      >
        <TextField
          size="small"
          fullWidth
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            // A stale verdict about the previous query is just noise.
            setMessage(null);
            // Emptying the box means "never mind" — take the pin down with it.
            // Merely editing does not: the operator refining a query is still
            // interested in where the last one landed (#523).
            if (e.target.value.trim() === '') {
              dispatch(searchPinCleared());
            }
          }}
          placeholder="Coordinates, address, postcode"
          inputProps={{ 'aria-label': 'Search location' }}
          aria-describedby={message ? 'map-search-message' : undefined}
        />
        <IconButton
          type="submit"
          size="small"
          color="primary"
          aria-label="Search"
          disabled={searching}
        >
          {searching ? <CircularProgress size={18} /> : <SearchIcon />}
        </IconButton>
      </Box>

      {searching && (
        <Typography variant="caption" color="text.secondary" role="status">
          Searching…
        </Typography>
      )}

      {message && !searching && (
        <Typography
          id="map-search-message"
          variant="caption"
          component="p"
          role={message.kind === 'error' ? 'alert' : 'status'}
          color={message.kind === 'error' ? 'error' : 'text.secondary'}
          sx={{ mt: 0.5, display: 'block', wordBreak: 'break-word' }}
        >
          {message.text}
        </Typography>
      )}
    </Paper>
  );
};
